import { detectPlatform } from '../platforms';
import { getPlatformHint } from '../platforms/detect';
import { getPlatformModule } from '../platforms';

export const LINK_SESSION_KEY = 'qc_account_link_session_token';
export const LINK_TARGET_KEY = 'qc_account_link_target_provider';
export const PROCESSED_LINK_CODES_KEY = 'qc_processed_link_codes';
export const LINK_START_PREFIX = 'link_';

/** @deprecated Cleared on link completion; kept for stale session cleanup. */
const LEGACY_LINK_TOKEN_KEY = 'qc_account_link_primary_token';

const PROCESSED_LINK_TTL_MS = 7 * 24 * 60 * 60 * 1000;

let linkCapturePromise = null;

export function getPlatformAppUrl(provider) {
  if (provider === 'telegram') {
    return process.env.REACT_APP_TELEGRAM_MINI_APP_URL || '';
  }
  if (provider === 'max') {
    return process.env.REACT_APP_MAX_APP_URL || '';
  }
  return '';
}

function readStartParamFromUrl() {
  if (typeof window === 'undefined') {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has('startapp')) {
    return searchParams.get('startapp');
  }

  const hash = window.location.hash?.slice(1) || '';
  if (!hash) {
    return null;
  }

  const hashParams = new URLSearchParams(hash);
  if (hashParams.has('startapp')) {
    return hashParams.get('startapp');
  }

  return null;
}

/** Read startapp / start_param from platform SDK or URL. */
export function readLaunchStartParam(platform = null) {
  const fromPlatform = platform?.getStartParam?.();
  if (fromPlatform) {
    return fromPlatform;
  }

  return readStartParamFromUrl();
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export async function waitForPlatformAuthPayload(platform, maxWaitMs = 8000) {
  const deadline = Date.now() + maxWaitMs;

  while (Date.now() < deadline) {
    const ready = platform?.hasAuthPayload?.() ?? Boolean(platform?.getAuthPayload?.());
    const payload = platform?.getAuthPayload?.();
    if (ready && payload) {
      return payload;
    }
    await sleep(200);
  }

  return null;
}

async function waitForLinkStartParam(platform, maxWaitMs = 10000) {
  const deadline = Date.now() + maxWaitMs;

  while (Date.now() < deadline) {
    const startParam = readLaunchStartParam(platform);
    const linkToken = parseLinkStartParam(startParam);
    if (linkToken) {
      return { startParam, linkToken };
    }
    await sleep(200);
  }

  return null;
}

export function parseLinkStartParam(startParam) {
  if (!startParam || !startParam.startsWith(LINK_START_PREFIX)) {
    return null;
  }

  const linkToken = startParam.slice(LINK_START_PREFIX.length).trim();
  return linkToken || null;
}

export function buildLinkStartParam(linkToken) {
  return `${LINK_START_PREFIX}${linkToken}`;
}

export function appendStartAppToUrl(baseUrl, startParam) {
  if (!baseUrl || !startParam) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl);
    url.searchParams.set('startapp', startParam);
    return url.toString();
  } catch {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}startapp=${encodeURIComponent(startParam)}`;
  }
}

function storePendingLink(linkToken, targetProvider) {
  sessionStorage.setItem(LINK_SESSION_KEY, linkToken);
  sessionStorage.setItem(LINK_TARGET_KEY, targetProvider);
}

function readProcessedLinkCodes() {
  if (typeof localStorage === 'undefined') {
    return {};
  }

  try {
    const raw = localStorage.getItem(PROCESSED_LINK_CODES_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeProcessedLinkCodes(codes) {
  if (typeof localStorage === 'undefined') {
    return;
  }

  localStorage.setItem(PROCESSED_LINK_CODES_KEY, JSON.stringify(codes));
}

function pruneProcessedLinkCodes(codes) {
  const cutoff = Date.now() - PROCESSED_LINK_TTL_MS;
  return Object.fromEntries(
    Object.entries(codes).filter(([, processedAt]) => processedAt >= cutoff),
  );
}

export function isLinkCodeProcessed(linkToken) {
  if (!linkToken) {
    return false;
  }

  const codes = pruneProcessedLinkCodes(readProcessedLinkCodes());
  writeProcessedLinkCodes(codes);
  return Boolean(codes[linkToken]);
}

export function markLinkCodeProcessed(linkToken) {
  if (!linkToken) {
    return;
  }

  const codes = pruneProcessedLinkCodes(readProcessedLinkCodes());
  codes[linkToken] = Date.now();
  writeProcessedLinkCodes(codes);
}

export function hasPendingAccountLinkSession() {
  return Boolean(getPendingAccountLink());
}

export function shouldCaptureAccountLink(platform = null) {
  if (hasPendingAccountLinkSession()) {
    return true;
  }

  const linkToken = parseLinkStartParam(readLaunchStartParam(platform));
  return Boolean(linkToken && !isLinkCodeProcessed(linkToken));
}

/** Capture link_* from URL before platform SDK init (race with token refresh). */
function tryEarlyCaptureAccountLink() {
  if (typeof window === 'undefined') {
    return false;
  }

  if (getPendingAccountLink()) {
    return true;
  }

  const startParam = readStartParamFromUrl();
  const linkToken = parseLinkStartParam(startParam);
  if (!linkToken || isLinkCodeProcessed(linkToken)) {
    return false;
  }

  const providerHint = getPlatformHint();
  if (providerHint !== 'telegram' && providerHint !== 'max') {
    return false;
  }

  storePendingLink(linkToken, providerHint);
  return true;
}

function captureAccountLinkFromLaunch(platform) {
  const startParam = readLaunchStartParam(platform);
  const linkToken = parseLinkStartParam(startParam);
  if (!linkToken || isLinkCodeProcessed(linkToken)) {
    return false;
  }

  const currentProvider = platform?.PROVIDER;
  if (!currentProvider) {
    return false;
  }

  storePendingLink(linkToken, currentProvider);
  return true;
}

async function ensureAccountLinkCaptured(platform) {
  if (captureAccountLinkFromLaunch(platform)) {
    return true;
  }

  const found = await waitForLinkStartParam(platform);
  if (!found || !platform?.PROVIDER) {
    return false;
  }

  storePendingLink(found.linkToken, platform.PROVIDER);
  return true;
}

async function prepareAccountLinkCapture(platform) {
  tryEarlyCaptureAccountLink();

  if (!platform) {
    return;
  }

  captureAccountLinkFromLaunch(platform);
  if (shouldCaptureAccountLink(platform)) {
    await ensureAccountLinkCaptured(platform);
  }
}

/** Init platform SDK once and capture link tokens from launch params. */
export async function ensureAccountLinkCaptureReady(platform) {
  if (!platform) {
    tryEarlyCaptureAccountLink();
    return;
  }

  if (!linkCapturePromise) {
    linkCapturePromise = (async () => {
      await platform.ensureReady?.();
      platform.init?.();
      await prepareAccountLinkCapture(platform);
    })();
  }

  await linkCapturePromise;
}

export async function startAccountLink(targetProvider, accessToken, authService) {
  if (!accessToken) {
    throw new Error('Нет активной сессии для привязки');
  }

  const targetUrl = getPlatformAppUrl(targetProvider);
  if (!targetUrl) {
    throw new Error(`URL приложения для ${targetProvider} не настроен`);
  }

  const { link_token: linkToken } = await authService.createLinkSession(accessToken);
  if (!linkToken) {
    throw new Error('Не удалось создать сессию привязки');
  }

  const linkUrl = appendStartAppToUrl(targetUrl, buildLinkStartParam(linkToken));
  const currentPlatform = detectPlatform();

  if (currentPlatform?.openApp) {
    currentPlatform.openApp(linkUrl);
    return;
  }

  window.open(linkUrl, '_blank', 'noopener,noreferrer');
}

export function clearPendingAccountLink() {
  sessionStorage.removeItem(LEGACY_LINK_TOKEN_KEY);
  sessionStorage.removeItem(LINK_SESSION_KEY);
  sessionStorage.removeItem(LINK_TARGET_KEY);
}

export function getPendingAccountLink() {
  const linkToken = sessionStorage.getItem(LINK_SESSION_KEY);
  const targetProvider = sessionStorage.getItem(LINK_TARGET_KEY);
  if (!targetProvider || !linkToken) {
    return null;
  }

  return { linkToken, targetProvider };
}

function mapLinkErrorMessage(error) {
  const message = error?.message || '';
  if (message.includes('истекла')) {
    return 'Ссылка для привязки истекла. Вернитесь в исходное приложение и начните заново.';
  }
  if (message.includes('уже привязан')) {
    return 'Этот аккаунт уже привязан к другому профилю.';
  }
  if (message.includes('уже привязана другая площадка')) {
    return 'К вашему профилю уже привязана другая учётная запись на этой площадке.';
  }
  if (message.includes('Невалидные данные')) {
    return 'Не удалось подтвердить аккаунт. Откройте приложение заново из мессенджера.';
  }
  return message || 'Не удалось привязать аккаунт';
}

export async function processPendingAccountLink(authService, currentProvider) {
  const pending = getPendingAccountLink();
  if (!pending || pending.targetProvider !== currentProvider) {
    return null;
  }

  if (isLinkCodeProcessed(pending.linkToken)) {
    clearPendingAccountLink();
    return null;
  }

  const platform = getPlatformModule(currentProvider);
  const initData = await waitForPlatformAuthPayload(platform);
  if (!initData) {
    throw new Error('Не удалось подтвердить аккаунт. Откройте приложение заново из мессенджера.');
  }

  try {
    const result = await authService.linkIdentity(currentProvider, initData, {
      linkToken: pending.linkToken,
      mergeIfTaken: true,
    });

    markLinkCodeProcessed(pending.linkToken);
    clearPendingAccountLink();

    if (result?.access_token) {
      authService.saveToken(result.access_token);
    } else {
      await authService.authenticateWithProvider(currentProvider, initData);
    }

    return result;
  } catch (error) {
    const message = error?.message || '';
    if (message.includes('уже использована')) {
      markLinkCodeProcessed(pending.linkToken);
      clearPendingAccountLink();
      if (authService.checkAuth()) {
        return null;
      }
    }

    clearPendingAccountLink();
    throw new Error(mapLinkErrorMessage(error));
  }
}

if (typeof window !== 'undefined') {
  tryEarlyCaptureAccountLink();
}
